import axios, { type CancelToken, type CancelTokenSource } from 'axios';
import { defineStore } from 'pinia';
import { computed, onScopeDispose, ref } from 'vue';
import { getEventDownloadSasUrl, type DownloadVariant } from '@/api/events';

export type { DownloadVariant };

export interface Download {
    id: string;
    completed: boolean;
    error?: string;
    progress: number;
    name: string;
    size: number;
    url?: string;
    eventId?: number;
    variant?: DownloadVariant;
    fallbackUrl?: string;
}

// Either a fully-resolved url, or enough to resolve a fresh SAS url from an
// eventId at the download's turn in the queue (falling back to `fallbackUrl`
// if SAS retrieval fails).
export type NewDownload = { name: string; size: number } & (
    | { url: string; eventId?: never; variant?: never; fallbackUrl?: never }
    | { eventId: number; variant: DownloadVariant; fallbackUrl?: string; url?: never }
);

// Space out the browser-level download triggers so we don't trip Chrome/Edge's
// "this site is trying to download multiple files" block, which can otherwise
// kick in when several downloads fire back-to-back with no user gesture between them.
const MIN_TRIGGER_INTERVAL_MS = 1200;

const VARIANT_LABEL: Record<DownloadVariant, string> = {
    original: 'Original',
    muted: 'Public',
};

function generateId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function buildFilename(name: string, url: string, variant?: DownloadVariant, mimeType?: string) {
    const baseName = variant ? `${name} (${VARIANT_LABEL[variant]})` : name;
    const safeName = (baseName || 'download').replace(/[\\/:*?"<>|]+/g, '-').trim();
    if (/\.[a-z0-9]{2,4}$/i.test(safeName)) {
        return safeName;
    }

    const extFromUrl = url?.split(/[#?]/)[0]?.split('.').pop();
    if (extFromUrl && extFromUrl.length <= 4 && /^[a-z0-9]+$/i.test(extFromUrl)) {
        return `${safeName}.${extFromUrl}`;
    }

    const extFromMime = mimeType?.split('/')[1];
    return `${safeName}.${extFromMime || 'mp4'}`;
}

function triggerBlobDownload(blob: Blob, filename: string) {
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Give the browser a moment to pick up the object URL before revoking it.
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30000);
}

function triggerNavigationDownload(url: string, filename: string) {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    // Cross-origin URLs cause browsers to ignore the `download` attribute and
    // navigate instead - opening in a new tab keeps that navigation off the app.
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export const useDownloadQueue = defineStore('downloadQueue', () => {
    // state
    const downloads = ref<Download[]>([]);
    const isLoading = ref(false);

    // per-download control that doesn't need to be reactive
    const abortControllers = new Map<string, AbortController>();
    const cancelTokenSources = new Map<string, CancelTokenSource>();
    let lastTriggerTime = 0;

    // getters
    const activeDownloads = computed<Download[]>(() => downloads.value.filter((download) => !download.completed && !download.error));
    const finishedDownloads = computed<Download[]>(() => downloads.value.filter((download) => download.completed && !download.error));
    const failedDownloads = computed<Download[]>(() => downloads.value.filter((download) => !!download?.error));
    const isDownloading = computed<boolean>(() => activeDownloads.value.length > 0);
    const currentDownloadProgress = computed<number>(() => activeDownloads.value[0]?.progress ?? 0);

    // actions
    function addDownload(download: NewDownload) {
        downloads.value.push({
            id: generateId(),
            name: download.name,
            size: download.size,
            url: download.url,
            eventId: download.eventId,
            variant: download.variant,
            fallbackUrl: download.fallbackUrl,
            completed: false,
            progress: 0,
        });
        startNextDownload();
    }

    // SAS tokens are short-lived, so this resolves at the download's turn in the
    // queue rather than when it's added - falling back to `fallbackUrl` (a stable
    // but historically-unreliable path) only if the fresh SAS lookup fails.
    async function resolveDownloadUrl(download: Download, cancelToken: CancelToken): Promise<string> {
        if (download.url) {
            return download.url;
        }
        if (!download.eventId || !download.variant) {
            throw new Error('Missing download source');
        }
        try {
            const sas = await getEventDownloadSasUrl(download.eventId, download.variant, cancelToken);
            if (!sas) {
                throw new Error('SAS url unavailable');
            }
            return sas;
        } catch (err) {
            if (download.fallbackUrl) {
                return download.fallbackUrl;
            }
            throw err;
        }
    }

    async function startNextDownload() {
        if (isDownloading.value && isLoading.value) {
            return;
        }
        const nextDownload = activeDownloads.value[0];
        if (!nextDownload) {
            return;
        }

        isLoading.value = true;

        const wait = MIN_TRIGGER_INTERVAL_MS - (Date.now() - lastTriggerTime);
        if (wait > 0) {
            await new Promise((resolve) => setTimeout(resolve, wait));
        }

        // Could have been cancelled while we were waiting out the trigger interval.
        if (!downloads.value.includes(nextDownload)) {
            isLoading.value = false;
            startNextDownload();
            return;
        }

        const cancelTokenSource = axios.CancelToken.source();
        cancelTokenSources.set(nextDownload.id, cancelTokenSource);

        try {
            const resolvedUrl = await resolveDownloadUrl(nextDownload, cancelTokenSource.token);

            try {
                await downloadWithProgress(nextDownload, resolvedUrl);
                nextDownload.completed = true;
                nextDownload.progress = 100;
            } catch (err) {
                // If it's no longer in the queue, it was cancelled (individually or via
                // cancelAllDownloads) - don't fall back to a real download in that case.
                if (downloads.value.includes(nextDownload)) {
                    // Fetch-based download failed - likely CORS or a network error.
                    // Fall back to a plain browser-native download without progress tracking.
                    try {
                        triggerNavigationDownload(resolvedUrl, buildFilename(nextDownload.name, resolvedUrl, nextDownload.variant));
                        nextDownload.completed = true;
                    } catch (fallbackErr) {
                        nextDownload.error = 'Download failed';
                        console.error(`Failed to download "${nextDownload.name}"`, err, fallbackErr);
                    }
                }
            }
        } catch (resolveErr) {
            // No fallback download is possible if we couldn't even resolve a url.
            if (downloads.value.includes(nextDownload)) {
                nextDownload.error = 'Download failed';
                console.error(`Failed to resolve download url for "${nextDownload.name}"`, resolveErr);
            }
        } finally {
            abortControllers.delete(nextDownload.id);
            cancelTokenSources.delete(nextDownload.id);
            lastTriggerTime = Date.now();
            isLoading.value = false;
            startNextDownload();
        }
    }

    async function downloadWithProgress(download: Download, url: string) {
        const controller = new AbortController();
        abortControllers.set(download.id, controller);

        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok || !response.body) {
            throw new Error(`Request for "${download.name}" failed with status ${response.status}`);
        }

        const totalBytes = Number(response.headers.get('Content-Length')) || download.size || 0;
        const reader = response.body.getReader();
        const chunks: BlobPart[] = [];
        let receivedBytes = 0;

        for (;;) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            chunks.push(value);
            receivedBytes += value.byteLength;
            if (totalBytes) {
                download.progress = Math.min(99, Math.round((receivedBytes / totalBytes) * 100));
            }
        }

        const blob = new Blob(chunks);
        triggerBlobDownload(blob, buildFilename(download.name, url, download.variant, blob.type));
    }

    function cancelAllDownloads() {
        abortControllers.forEach((controller) => controller.abort());
        abortControllers.clear();
        cancelTokenSources.forEach((source) => source.cancel('Download cancelled'));
        cancelTokenSources.clear();
        downloads.value = [];
        isLoading.value = false;
    }
    function cancelDownload(download: Download) {
        abortControllers.get(download.id)?.abort();
        cancelTokenSources.get(download.id)?.cancel('Download cancelled');
        const index = downloads.value.indexOf(download);
        if (index >= 0) {
            downloads.value.splice(index, 1);
        }
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
        if (isDownloading.value) {
            event.preventDefault();
            // Older browsers require setting returnValue to truthy value, but the text is generally ignored.
            event.returnValue = 'There are pending downloads. Are you sure you want to leave?';
        }
    };

    // Add the event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Clean up the listener when the store is disposed
    onScopeDispose(() => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
    });
    return {
        downloads,
        isLoading,
        currentDownloadProgress,
        activeDownloads,
        finishedDownloads,
        failedDownloads,
        isDownloading,
        addDownload,
        cancelAllDownloads,
        cancelDownload,
    }
  }
);
